import React from 'react';

export const ArticleDarcy: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <article className="bg-white rounded-3xl p-8 sm:p-12 space-y-8 shadow-xs border border-slate-200">
        <header className="border-b border-slate-100 pb-6">
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-teal-50 text-[#087F9C]">
            Fluid Mechanics Guide
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] mt-3">
            The Darcy-Weisbach Equation for Pipe Friction Loss &amp; Head Drop
          </h1>
          <div className="text-xs text-slate-400 mt-2 flex flex-wrap gap-4">
            <span>Reviewed by PipeSizingCalculator Engineering Team</span>
            <span>Updated October 31, 2026</span>
            <span>Reading Time: 7 min</span>
          </div>
        </header>

        <section className="space-y-4 text-sm text-slate-700 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D]">1. Physical Formulation of Pipe Friction Loss</h2>
          <p>
            The Darcy-Weisbach equation is the fundamental theoretical relationship governing head loss due to friction in pipe flow:
          </p>
          <div className="p-4 bg-slate-50 rounded-xl font-mono text-xs border border-slate-200 text-center font-bold text-[#10283D]">
            h_f = f &middot; (L / D) &middot; (v&sup2; / 2g) &emsp; or &emsp; &Delta;P = f &middot; (L / D) &middot; (&rho; &middot; v&sup2; / 2)
          </div>
          <p>
            Where:
            <br />&bull; h_f: Head loss in meters of fluid column (m)
            <br />&bull; f: Dimensionless Darcy friction factor
            <br />&bull; L: Pipe length (m)
            <br />&bull; D: Internal pipe diameter (m)
            <br />&bull; v: Mean flow velocity (m/s)
            <br />&bull; &rho;: Fluid density (kg/m&sup3;)
            <br />&bull; g: Acceleration due to gravity (9.80665 m/s&sup2;)
          </p>

          <h2 className="font-display font-bold text-xl text-[#10283D] pt-4">2. Darcy-Weisbach vs Hazen-Williams</h2>
          <p>
            While Hazen-Williams is widely cited in civil water mains, it is an empirical approximation calibrated strictly for cold water at ~15&deg;C. For HVAC heating (60&deg;C&ndash;80&deg;C) or chilled water (4&deg;C&ndash;6&deg;C), viscosity shifts cause Hazen-Williams errors exceeding 20%. The Darcy-Weisbach equation is universally valid across all fluid temperatures and viscosity regimes.
          </p>

          <h2 className="font-display font-bold text-xl text-[#10283D] pt-4">3. Explicit Swamee-Jain Friction Factor</h2>
          <p>
            In turbulent flow (Re &ge; 4000), the implicit Colebrook-White equation is solved accurately without iterations via the Swamee-Jain formula:
          </p>
          <div className="p-4 bg-slate-50 rounded-xl font-mono text-xs border border-slate-200 text-center font-bold text-[#10283D]">
            f = 0.25 / [ log10( (&epsilon; / 3.7D) + (5.74 / Re^0.9) ) ]&sup2;
          </div>
        </section>

        <footer className="pt-6 border-t border-slate-100 flex flex-wrap gap-3 text-xs font-semibold">
          <a href="/pipe-pressure-drop-calculator/" className="px-4 py-2 bg-[#087F9C] text-white rounded-xl hover:bg-teal-700">Calculate Friction Loss Live &rarr;</a>
          <a href="/water-pipe-sizing-calculator/" className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200">Water Pipe Sizing Tool &rarr;</a>
          <a href="/guides/pipe-sizing-formula/" className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200">Pipe Sizing Formula &rarr;</a>
        </footer>
      </article>
    </div>
  );
};
